import * as DataV2 from '../api/archibus-v2/DataApi'
import * as Document from '../api/archibus/DocumentApi'
import {departmentColors, roomTypeColors} from '../utils/colors'
import meetingRoomPlaceholder from '../assets/img/meetingroom-placeholder-essential.png'
import {fetchLocationEmployee} from './employees'

export const getRoomPhotoUrl = ({bl_id, fl_id, rm_id}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm.bl_id', bl_id)
  restriction.addClause('rm.fl_id', fl_id)
  restriction.addClause('rm.rm_id', rm_id)
  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'roomsWithPhotos',
    restriction
  ).then(result => {
    const [room] = result.data
    if (room && room.rmPhoto) {
      return Document.getTempDocumentUrl(
        'rm',
        {bl_id, fl_id, rm_id},
        'rm_photo'
      )
    } else {
      return Promise.resolve(null)
    }
  })
}

export const getRoomAmenities = ({bl_id, fl_id, rm_id, rm_arrange_type_id}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm_resource_std.bl_id', bl_id)
  restriction.addClause('rm_resource_std.fl_id', fl_id)
  restriction.addClause('rm_resource_std.rm_id', rm_id)

  if (rm_arrange_type_id) {
    restriction.addClause(
      'rm_resource_std.rm_arrange_type_id',
      rm_arrange_type_id
    )
  }

  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'roomResources',
    restriction
  ).then(result => {
    const {data} = result
    let amenities = data.map(amenity => ({
      resourceStd: amenity.resourceStd,
      name: amenity.resourceStdResourceName
    }))
    return Promise.resolve(amenities)
  })
}

const getDistinctAmenities = data => {
  const result = []
  const map = new Map()
  data.forEach(item => {
    const {resourceStd, resourceStdResourceName} = item
    if (!map.has(resourceStd)) {
      map.set(resourceStd, true)
      result.push({
        value: resourceStd,
        title: resourceStdResourceName
      })
    }
  })

  return result
}
export const fetchAmenitiesForFloor = ({blId, flId}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm_resource_std.bl_id', blId)
  restriction.addClause('rm_resource_std.fl_id', flId)

  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'roomResources',
    restriction
  ).then(({data}) => Promise.resolve(getDistinctAmenities(data)))
}

export const getRoomArrangement = ({bl_id, fl_id, rm_id}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm_arrange.bl_id', bl_id)
  restriction.addClause('rm_arrange.fl_id', fl_id)
  restriction.addClause('rm_arrange.rm_id', rm_id)

  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'roomArrangements',
    restriction
  ).then(({data}) => {
    const [arrangement] = data
    return Promise.resolve(arrangement)
  })
}

// Simple Room cache
const roomCache = {}

export const fetchRoom = ({blId, flId, rmId}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm.bl_id', blId)
  restriction.addClause('rm.fl_id', flId)
  restriction.addClause('rm.rm_id', rmId)

  const roomKey = `${blId}${flId}${rmId}`

  if (roomCache[roomKey]) {
    return Promise.resolve(roomCache[roomKey])
  } else {
    return DataV2.getDataFromDataSource(
      'ab-self-service.axvw',
      'rooms',
      restriction
    ).then(async result => {
      const {data} = result
      let [room] = data
      // Set the photo URL
      let url
      if (room.rmPhoto) {
        url = await getRoomPhotoUrl({bl_id: blId, fl_id: flId, rm_id: rmId})
      }
      room.url = url
      room.roomCode = `${room.blId};${room.flId};${room.rmId}`
      roomCache[roomKey] = room
      return Promise.resolve(room)
    })
  }
}

export const fetchIsRoomReservable = ({blId, flId, rmId}) => {
  return fetchRoom({blId, flId, rmId}).then(room =>
    Promise.resolve(room.reservable === 1)
  )
}

export const fetchRooms = ({blId = null, flId = null} = {}) => {
  let restriction = null

  if (blId || flId) {
    restriction = new Ab.view.Restriction()
  }

  if (blId) {
    restriction.addClause('rm.bl_id', blId)
  }

  if (flId) {
    restriction.addClause('rm.fl_id', flId)
  }

  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'rooms',
    restriction
  ).then(result => {
    const {data} = result
    let rooms = data.reduce((acc, room) => {
      const {blId, flId, rmId} = room
      let roomId = `${blId}${flId}${rmId}`
      acc[roomId] = room
      return acc
    }, {})
    return Promise.resolve(rooms)
  })
}

export const fetchRoomsByDepartment = ({blId, flId}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm.bl_id', blId)
  restriction.addClause('rm.fl_id', flId)

  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'rooms',
    restriction
  ).then(result => {
    const {data: rooms} = result

    // Rooms By Department
    let roomsByDepartment = rooms.reduce((acc, room) => {
      const {deptCode, roomCode} = room
      if (acc[deptCode] && acc[deptCode].rooms) {
        acc[deptCode].rooms.push(roomCode)
      } else {
        acc[deptCode] = {rooms: []}
        acc[deptCode].rooms.push(roomCode)
      }
      return acc
    }, {})

    let colorIdx = 0
    // Create a set to save the department names and check for duplicates
    let deptSet = new Set()

    let departmentsByFloor = rooms.reduce((acc, curVal) => {
      const {deptCode, dvId, dpId, dpName, dvName} = curVal
      if (!acc[deptCode]) {
        let duplicate = false
        if (deptSet.has(dpName)) {
          duplicate = true

          // Find other objects in the accumulator that have the same department name but that are not duplicates
          // and set the duplicate value
          Object.values(acc)
            .filter(
              item => item.department === dpName && item.duplicate === false
            )
            .forEach(dept => {
              dept.duplicate = true
            })
        } else {
          deptSet.add(dpName)
        }

        acc[deptCode] = {
          dv_id: dvId,
          dp_id: dpId,
          department: dpName,
          division: dvName,
          color: departmentColors[colorIdx % departmentColors.length],
          deptCode,
          duplicate,
          name: `${dpName} - ${dvName}`
        }
        colorIdx += 1
      }

      return acc
    }, {})

    // Add the room list to departmentsByFloor
    Object.keys(roomsByDepartment).forEach(deptCode => {
      if (departmentsByFloor[deptCode]) {
        departmentsByFloor[deptCode].rooms = roomsByDepartment[deptCode].rooms
      } else {
        departmentsByFloor[deptCode].rooms = []
      }
    })

    return Promise.resolve(departmentsByFloor)
  })
}

export const fetchRoomTypes = ({blId, flId}) => {
  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'roomTypeList',
    null,
    [{name: 'bl_id', value: blId}, {name: 'fl_id', value: flId}]
  ).then(({data}) => {
    return Promise.resolve(data)
  })
}

export const fetchRoomTypesToHighlight = ({blId, flId}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm.bl_id', blId)
  restriction.addClause('rm.fl_id', flId)

  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'useHighlight',
    restriction
  ).then(result => {
    const {data: rooms} = result

    // Rooms By room type
    let roomsByRoomType = rooms.reduce((acc, room) => {
      const {rmType, roomCode} = room
      if (acc[rmType] && acc[rmType].rooms) {
        acc[rmType].rooms.push(roomCode)
      } else {
        acc[rmType] = {rooms: []}
        acc[rmType].rooms.push(roomCode)
      }
      return acc
    }, {})

    let colorIdx = 0
    // Create a set to save the room type names and check for duplicates
    let rmTypeSet = new Set()

    let roomTypesByFloor = rooms.reduce((acc, curVal) => {
      const {rmType, rmTypeDesc} = curVal
      if (!acc[rmType]) {
        let duplicate = false
        if (rmTypeSet.has(rmTypeDesc)) {
          duplicate = true

          // Find other objects in the accumulator that have the same rmType desc but that are not duplicates
          // and set the duplicate value
          Object.values(acc)
            .filter(
              item => item.rmTypeDesc === rmTypeDesc && item.duplicate === false
            )
            .forEach(rmType => {
              rmType.duplicate = true
            })
        } else {
          rmTypeSet.add(rmTypeDesc)
        }
        colorIdx = roomTypeColors.findIndex(x => x.roomCat === rmType.substring(0,3))
        if (colorIdx == -1){
        	colorIdx = 7
        }
        acc[rmType] = {
          rmTypeDesc: rmTypeDesc,
          color: roomTypeColors[colorIdx],
          rmType,
          duplicate
        }
      }

      return acc
    }, {})

    // Add the room list to roomTypesByFloor
    Object.keys(roomsByRoomType).forEach(rmType => {
      if (roomTypesByFloor[rmType]) {
        roomTypesByFloor[rmType].rooms = roomsByRoomType[rmType].rooms
      } else {
        roomTypesByFloor[rmType].rooms = []
      }
    })

    return Promise.resolve(roomTypesByFloor)
  })
}
/*
export const fetchRoomTypesToHighlight = ({blId, flId}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('rm.bl_id', blId)
  restriction.addClause('rm.fl_id', flId)
  restriction.addClause('rm.rm_cat', '110', '=', ')AND(')
  restriction.addClause('rm.rm_cat', '210', '=', 'OR')
  restriction.addClause('rm.rm_cat', '350', '=', 'OR')
  restriction.addClause('rm.rm_type', 'XXX03', '=', 'OR')
  restriction.addClause('rm.rm_type', 'XXX04', '=', 'OR')
  restriction.addClause('rm.rm_type', 'XXX05', '=', 'OR')

  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'rooms',
    restriction
  ).then(result => {
    const {data: rooms} = result

    // Rooms By room type
    let roomsByRoomType = rooms.reduce((acc, room) => {
      const {rmType, roomCode} = room
      if (acc[rmType] && acc[rmType].rooms) {
        acc[rmType].rooms.push(roomCode)
      } else {
        acc[rmType] = {rooms: []}
        acc[rmType].rooms.push(roomCode)
      }
      return acc
    }, {})

    let colorIdx = 0
    // Create a set to save the room type names and check for duplicates
    let rmTypeSet = new Set()

    let roomTypesByFloor = rooms.reduce((acc, curVal) => {
      const {rmType, rmTypeDesc} = curVal
      if (!acc[rmType]) {
        let duplicate = false
        if (rmTypeSet.has(rmTypeDesc)) {
          duplicate = true

          // Find other objects in the accumulator that have the same rmType desc but that are not duplicates
          // and set the duplicate value
          Object.values(acc)
            .filter(
              item => item.rmTypeDesc === rmTypeDesc && item.duplicate === false
            )
            .forEach(rmType => {
              rmType.duplicate = true
            })
        } else {
          rmTypeSet.add(rmTypeDesc)
        }
        colorIdx = roomTypeColors.findIndex(x => x.roomCat === rmType.substring(0,3))
        if (colorIdx == -1){
        	colorIdx = 4
        }
        acc[rmType] = {
          rmTypeDesc: rmTypeDesc,
          color: roomTypeColors[colorIdx],
          rmType,
          duplicate
        }
      }

      return acc
    }, {})

    // Add the room list to roomTypesByFloor
    Object.keys(roomsByRoomType).forEach(rmType => {
      if (roomTypesByFloor[rmType]) {
        roomTypesByFloor[rmType].rooms = roomsByRoomType[rmType].rooms
      } else {
        roomTypesByFloor[rmType].rooms = []
      }
    })

    return Promise.resolve(roomTypesByFloor)
  })
}
*/
export const fetchRoomInfo = async ({blId, flId, rmId, rmArrangeTypeId}) => {
  const amenities = await getRoomAmenities({
    bl_id: blId,
    fl_id: flId,
    rm_id: rmId,
    rm_arrange_type_id: rmArrangeTypeId
  })

  const {minRequired, maxCapacity, rmRmPhoto} = await getRoomArrangement({
    bl_id: blId,
    fl_id: flId,
    rm_id: rmId
  })

  let url
  if (rmRmPhoto) {
    url = await getRoomPhotoUrl({bl_id: blId, fl_id: flId, rm_id: rmId})
  }

  url = url || meetingRoomPlaceholder

  return {
    amenities,
    minRequired,
    maxCapacity,
    photoUrl: url
  }
}

export const fetchRoomWithEmployees = async ({blId, flId, rmId}) => {
  const room = await fetchRoom({blId, flId, rmId})
  const employees = await fetchLocationEmployee({blId, flId, rmId})

  return {
    ...room,
    employees: [...employees]
  }
}
